import { useEffect, useState, useMemo, useContext } from 'react'
import { useIntl, FormattedMessage } from 'react-intl'
import { Column, SortingRule } from 'react-table'
import { stateEntityAdapter } from '@racepointenergy/swc/scs/features/host-states'
import { dealerMonitorStates } from '@racepointenergy/swc/scs/dealer-monitor-states'
import type { HomeId } from '@racepointenergy/swc/scs/types'

import { getSortOptions } from 'components/Table/utils'
import { useAppDispatch, useAppSelector } from 'app/hooks'
import { homeActiveConfigEnergySelector } from 'features/home/selectors'
import type { Circuit } from 'features/home/types'
import { LoadCell, PowerCell, StateCell } from './cells'
import LoadStatusBlock from './LoadStatusBlock'
import {
  fetchHomeActiveConfigEnergyThunk,
  homeActiveConfigEnergyActions,
} from '../slice'
import {
  CommonSearch,
  Table,
  TableLoader,
  MobileSortBy,
} from 'components/Table'
import { messages } from '../messages'
import {
  LoadStatusWrapper,
  LoadStatusHeader,
  LoadStatusSearchWrapper,
} from './styles'
import { HomeStateContext } from 'features/home/homeStateContext'

const hostStateInitialState = stateEntityAdapter.getInitialState()

interface LoadStatusProps {
  homeId: HomeId
}

function LoadStatus({ homeId }: LoadStatusProps) {
  const { formatMessage } = useIntl()
  const { isOnline } = useContext(HomeStateContext)
  const dispatch = useAppDispatch()
  const [energyData, setEnergyData] = useState<Circuit[]>([])
  const [searchValue, setSearchValue] = useState<string>()
  const isHomeActiveConfigEnergyLoaded = useAppSelector(
    homeActiveConfigEnergySelector.isFinished
  )
  const homeActiveConfigEnergy = useAppSelector(
    homeActiveConfigEnergySelector.data
  )

  const {
    data = hostStateInitialState,
    isError,
    isFetching,
    error,
  } = dealerMonitorStates.useQuery({ id: homeId })

  useEffect(() => {
    if (homeId && !isHomeActiveConfigEnergyLoaded) {
      dispatch(fetchHomeActiveConfigEnergyThunk(homeId))
    }
  }, [dispatch, homeId, isHomeActiveConfigEnergyLoaded])

  useEffect(() => {
    if (isError) {
      console.log('Error getting system states: ', error)
    }
  }, [isError, error])

  useEffect(() => {
    if (
      homeActiveConfigEnergy?.energyConfig?.circuits?.length &&
      data?.entities
    ) {
      const e = homeActiveConfigEnergy.energyConfig.circuits.map((circuit) => {
        return {
          ...circuit,
          power:
            data.entities[`Energy.Circuit.${circuit.name}.Power`]?.value ?? 0,
          state: data.entities[`Energy.Circuit.${circuit.name}.Status`]?.value
            ? data.entities[`Energy.Circuit.${circuit.name}.Status`]?.value &
              0b1
            : -1,
          status:
            data.entities[`Energy.Circuit.${circuit.name}.StatusName`]?.value ??
            '-',
        }
      })
      setEnergyData(e)
    }
  }, [data, homeActiveConfigEnergy, setEnergyData])

  useEffect(() => {
    return () => {
      dispatch(homeActiveConfigEnergyActions.clear())
    }
  }, [dispatch, homeId])

  const headers = useMemo(
    () => ({
      load: {
        label: formatMessage(messages.headerLoad),
        id: 'Load',
      },
      consuming: {
        label: formatMessage(messages.headerConsuming),
        id: 'Consuming',
      },
      state: {
        label: formatMessage(messages.headerState),
        id: 'State',
      },
      status: {
        label: formatMessage(messages.headerStatus),
        id: 'Status',
      },
    }),
    [formatMessage]
  )

  const columns: readonly Column<Circuit>[] = useMemo(
    () => [
      {
        id: headers.load.id,
        Header: headers.load.label,
        accessor: (prop) => prop.name,
        Cell: LoadCell,
        percentWidth: '35%',
      },
      {
        id: headers.consuming.id,
        Header: headers.consuming.label,
        accessor: (row) => row.power,
        Cell: PowerCell,
        percentWidth: '20%',
      },
      {
        id: headers.state.id,
        Header: headers.state.label,
        accessor: (row) => row.state,
        Cell: StateCell,
        percentWidth: '10%',
      },
      {
        id: headers.status.id,
        Header: headers.status.label,
        accessor: (row) => row.status,
        percentWidth: '35%',
      },
    ],
    [headers]
  )
  const sortOptions = useMemo(() => getSortOptions(headers, true), [headers])
  const defaultSortBy = sortOptions[1]
  const [sortBy, setSortBy] = useState<SortingRule<Circuit>>(defaultSortBy)

  return (
    <LoadStatusWrapper>
      <LoadStatusHeader>
        <FormattedMessage {...messages.loadStatusHeader} />
      </LoadStatusHeader>
      <LoadStatusSearchWrapper>
        <CommonSearch
          setSearchValue={setSearchValue}
          placeholder={formatMessage(messages.searchPlaceholder)}
        />
      </LoadStatusSearchWrapper>
      <MobileSortBy
        headers={headers}
        sortOptions={sortOptions}
        sortBy={sortBy}
        setSortBy={setSortBy}
      />
      <div>
        {isFetching && isOnline && <TableLoader />}

        {(!isFetching || !isOnline) && (
          <div>
            <Table
              columns={columns}
              data={energyData}
              defaultSortBy={defaultSortBy}
              sortBy={sortBy}
              setSortBy={setSortBy}
              searchValue={searchValue}
              mobileViewComponent={LoadStatusBlock}
            />
          </div>
        )}
      </div>
    </LoadStatusWrapper>
  )
}

export default LoadStatus
